import React, { useState, useEffect } from 'react';
import LoginPage from './LoginPage';
import CoachDashboard from './CoachDashboard';
import AthleteTrainingApp from './AthleteTrainingApp';
import SubscriptionGate from './SubscriptionGate';
import { onAuthChange } from './auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase-config';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSubscription, setNeedsSubscription] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (currentUser) => {
      if (currentUser) {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const success = urlParams.get('success');

        if (success && sessionId) {
          try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
              subscription_status: 'active',
              subscription_session_id: sessionId,
              subscription_date: new Date(),
              updated_at: new Date()
            });
            currentUser.subscription_status = 'active';
            window.history.replaceState({}, document.title, window.location.pathname);
            alert('🎉 Welcome to Coach\'s Log! Your subscription is now active.');
          } catch (error) {
            console.error('Error updating subscription:', error);
          }
        }

        setUser(currentUser);

        if (currentUser.role === 'coach' && currentUser.subscription_status !== 'active') {
          setNeedsSubscription(true);
        } else {
          setNeedsSubscription(false);
        }
      } else {
        setUser(null);
        setNeedsSubscription(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (loggedInUser) => {
    const userDoc = await getDoc(doc(db, 'users', loggedInUser.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};
    const fullUser = { ...loggedInUser, ...userData };
    setUser(fullUser);
    if (fullUser.role === 'coach' && fullUser.subscription_status !== 'active') {
      setNeedsSubscription(true);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setNeedsSubscription(false);
  };

  const handleSubscribed = () => {
    setNeedsSubscription(false);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '48px',
          textAlign: 'center',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: '4px solid #e2e8f0',
            borderTopColor: '#2563eb',
            margin: '0 auto 24px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#64748b', fontSize: '16px', margin: 0, fontWeight: '500' }}>
            Loading Coach's Log...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) return <LoginPage onLogin={handleLogin} />;

  if (user.role === 'coach' && needsSubscription) {
    return <SubscriptionGate user={user} onSubscribed={handleSubscribed} />;
  }

  if (user.role === 'coach') return <CoachDashboard user={user} onLogout={handleLogout} />;

  if (user.role === 'athlete') return <AthleteTrainingApp user={user} onLogout={handleLogout} />;

  return <LoginPage onLogin={handleLogin} />;
}

export default App;